// src/hooks/usePosts.js
import { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  setDoc,
  getDocs,
  arrayUnion,
  arrayRemove,
  increment,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

function postsCol(orgId) {
  return collection(db, "organizations", orgId, "pinchpoints");
}

function postDoc(orgId, id) {
  return doc(db, "organizations", orgId, "pinchpoints", id);
}

function isActive(p) {
  if (p.status) return p.status === "open" || p.status === "investigating";
  return !p.resolved;
}

function isClosed(p) {
  if (p.status) return p.status === "resolved" || p.status === "dismissed";
  return !!p.resolved;
}

export function usePosts(orgId) {
  const [posts, setPosts] = useState([]);
  const [resolvedPosts, setResolvedPosts] = useState([]);
  const [status, setStatus] = useState("connecting");

  useEffect(() => {
    if (!orgId) return;
    const q = query(postsCol(orgId), orderBy("timestamp", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setStatus("live");
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPosts(all.filter(isActive));
        setResolvedPosts(all.filter(isClosed));
      },
      (err) => {
        console.error(err);
        setStatus("error");
      }
    );
    return unsub;
  }, [orgId]);

  const addPost = async (data) => {
    await addDoc(postsCol(orgId), {
      ...data,
      upvotes: 0,
      upvotedBy: [],
      status: "open",
      resolved: false,
      timestamp: serverTimestamp(),
    });
  };

  const deletePost = async (id) => {
    await deleteDoc(postDoc(orgId, id));
  };

  const flagPost = async (id, uid) => {
    await updateDoc(postDoc(orgId, id), {
      flagged: true,
      flaggedBy: arrayUnion(uid),
    });
  };

  const dismissFlag = async (id) => {
    // Delete all report docs from the subcollection
    const reportsRef = collection(db, "organizations", orgId, "pinchpoints", id, "reports");
    const snap = await getDocs(reportsRef);
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    await updateDoc(postDoc(orgId, id), { flagged: false, flaggedBy: [] });
  };

  const reportPost = async (id, uid, userName, reason) => {
    const reportRef = doc(db, "organizations", orgId, "pinchpoints", id, "reports", uid);
    await setDoc(reportRef, {
      reason,
      reportedAt: serverTimestamp(),
      reportedBy: uid,
      reportedByName: userName,
    });
    await updateDoc(postDoc(orgId, id), {
      flagged: true,
      flaggedBy: arrayUnion(uid),
    });
  };

  const upvotePost = async (id, uid, hasVoted) => {
    await updateDoc(postDoc(orgId, id), {
      upvotes: increment(hasVoted ? -1 : 1),
      upvotedBy: hasVoted ? arrayRemove(uid) : arrayUnion(uid),
    });
  };

  const updatePostStatus = async (postId, newStatus, { note = null, reason = null, actorUid = null, actorName = null } = {}) => {
    const batch = writeBatch(db);
    const pRef = postDoc(orgId, postId);
    const histRef = doc(collection(db, "organizations", orgId, "pinchpoints", postId, "statusHistory"));

    const update = { status: newStatus, statusUpdatedAt: serverTimestamp() };

    if (newStatus === "resolved") {
      update.resolved = true;
      update.resolvedAt = serverTimestamp();
      if (note) update.resolutionNote = note;
      if (actorUid) update.resolvedByUid = actorUid;
    } else if (newStatus === "dismissed") {
      update.resolved = false;
      update.dismissedAt = serverTimestamp();
      if (reason) update.dismissalReason = reason;
      if (note) update.dismissalNote = note;
      if (actorUid) update.dismissedByUid = actorUid;
    } else if (newStatus === "investigating") {
      update.resolved = false;
    } else if (newStatus === "open") {
      update.resolved = false;
      if (actorUid) update.reopenedByUid = actorUid;
    }

    batch.update(pRef, update);
    batch.set(histRef, {
      toStatus: newStatus,
      note: note || null,
      reason: reason || null,
      actorUid: actorUid || null,
      actorName: actorName || null,
      changedAt: serverTimestamp(),
    });

    await batch.commit();
  };

  return { posts, resolvedPosts, status, addPost, deletePost, upvotePost, flagPost, dismissFlag, reportPost, updatePostStatus };
}
